(function () {
  'use strict';

  var registry = window.TodayMerchPreviewRenderers = window.TodayMerchPreviewRenderers || {};
  if (registry.doming) return;

  var LIMITS = Object.freeze({ low: 320, medium: 640, high: 960, maxPixels: 16777216, maxDimension: 8192 });
  var renderMetadata = new WeakMap();

  function number(value, fallback, min, max) {
    return typeof value === 'number' && Number.isFinite(value) ? Math.max(min, Math.min(max, value)) : fallback;
  }

  function isCanvas(value) {
    var view = value && value.ownerDocument && value.ownerDocument.defaultView;
    return !!(view && value instanceof view.HTMLCanvasElement);
  }

  function canvas(document, width, height) {
    var result = document.createElement('canvas');
    result.width = width; result.height = height;
    return result;
  }

  function check(signal) {
    if (signal && signal.aborted) throw new DOMException('The doming preview was superseded.', 'AbortError');
  }

  function pause(signal) {
    return new Promise(function (resolve) { setTimeout(resolve, 0); }).then(function () { check(signal); });
  }

  function context(target) {
    var result = target.getContext('2d', { willReadFrequently: true });
    if (!result) throw new Error('A 2D canvas context is required for the doming preview.');
    return result;
  }

  function lightOptions(options) {
    var light = options.lighting && typeof options.lighting === 'object' ? options.lighting : {};
    var direction = Array.isArray(light.direction) ? light.direction : [];
    var x = number(light.x, number(light.directionX, number(direction[0], -0.45, -1, 1), -1, 1), -1, 1);
    var y = number(light.y, number(light.directionY, number(direction[1], -0.7, -1, 1), -1, 1), -1, 1);
    var length = Math.hypot(x, y);
    return { x: length ? x / length : 0, y: length ? y / length : 0,
      intensity: number(light.intensity, number(light.strength, 0.7, 0, 1), 0, 1) };
  }

  function eligible(options) {
    var technique = options.techniqueId || options.technique;
    if (technique && technique !== 'doming' && !/^DO\d+$/.test(technique)) return false;
    if (options.qualified === false) return false;
    var qualification = options.qualification;
    if (qualification && (qualification.compatible === false
      || qualification.status !== undefined && qualification.status !== 'allowed')) return false;
    return true;
  }

  function describe(options) {
    options = options && typeof options === 'object' ? options : {};
    var allowed = eligible(options), shape = options.labelShape && options.labelShape.qualified === true;
    return Object.freeze({ renderer: 'doming', version: '1.1.0', simulationOnly: true, previewOnly: true,
      applied: allowed, reason: allowed ? null : 'technique-or-qualification-incompatible',
      status: allowed ? 'simulated' : 'placement-only',
      method: allowed ? shape ? 'qualified-label-resin' : 'artwork-silhouette-gloss' : 'unchanged-artwork',
      geometryPreserved: true, effectiveFileChanged: false,
      limitations: Object.freeze(['illustrative-lighting', 'resin-height-not-calibrated', 'manufacturing-not-validated'].concat(shape ? [] : ['label-shape-not-qualified'])) });
  }

  function annotated(output, options, status) {
    var data = describe(options);
    if (status) data = Object.freeze(Object.assign({}, data, { applied: false, status: 'placement-only', method: 'unchanged-artwork', reason: status }));
    renderMetadata.set(output, data);
    Object.defineProperty(output, 'previewMetadata', { value: data, enumerable: false });
    return output;
  }

  function substrateLuminance(value) {
    // No color name, CSS expression, URL or unverified material is interpreted.
    var match = typeof value === 'string' && /^#([0-9a-f]{6})$/i.exec(value);
    if (!match) return 0.5;
    return (parseInt(match[1].slice(0, 2), 16) * 0.2126 + parseInt(match[1].slice(2, 4), 16) * 0.7152
      + parseInt(match[1].slice(4, 6), 16) * 0.0722) / 255;
  }

  function traceShape(ctx, shape, width, height) {
    if (['ellipse', 'rounded-rectangle', 'rectangle'].indexOf(shape.kind) === -1) {
      throw new TypeError('Unsupported qualified label shape.');
    }
    var x = shape.x, y = shape.y, w = shape.width, h = shape.height;
    if (![x, y, w, h].every(function (value) { return typeof value === 'number' && Number.isFinite(value); })
      || x < 0 || y < 0 || w <= 0 || h <= 0 || x + w > 1 || y + h > 1) {
      throw new RangeError('Qualified label bounds must fit the canvas in normalized coordinates.');
    }
    x *= width; y *= height; w *= width; h *= height;
    ctx.beginPath();
    if (shape.kind === 'ellipse') ctx.ellipse(x + w / 2, y + h / 2, w / 2, h / 2, 0, 0, Math.PI * 2);
    else if (shape.kind === 'rounded-rectangle') {
      var radius = number(shape.radius, 0, 0, 0.5) * Math.min(width, height);
      ctx.roundRect(x, y, w, h, Math.min(radius, w / 2, h / 2));
    } else ctx.rect(x, y, w, h);
  }

  function shapeMask(shape, document, width, height, sourceWidth, sourceHeight) {
    if (!shape || shape.qualified !== true) return null;
    var result = canvas(document, width, height), ctx = context(result);
    var fullMask = canvas(document, sourceWidth, sourceHeight), fullContext = context(fullMask);
    if (shape.kind === 'mask') {
      if (!isCanvas(shape.canvas) || shape.canvas.width !== sourceWidth || shape.canvas.height !== sourceHeight) {
        throw new TypeError('A qualified label mask must be a same-size canvas.');
      }
      fullContext.drawImage(shape.canvas, 0, 0);
    } else {
      traceShape(fullContext, shape, sourceWidth, sourceHeight);
      fullContext.fillStyle = '#ffffff'; fullContext.fill();
    }
    ctx.drawImage(fullMask, 0, 0, width, height);
    return { pixels: ctx.getImageData(0, 0, width, height).data, fullMask: fullMask };
  }

  async function validateShape(source, mask, signal) {
    // Validate the original grid: a one-pixel detached mark can disappear in the
    // lighting thumbnail and must never be accepted outside a real label cutline.
    var sourceContext = context(source), maskContext = mask && context(mask), lastYield = performance.now(), hasArtwork = false;
    for (var y = 0; y < source.height; y += 32) {
      var rows = Math.min(32, source.height - y);
      var art = sourceContext.getImageData(0, y, source.width, rows).data;
      var shape = maskContext && maskContext.getImageData(0, y, source.width, rows).data;
      for (var i = 3; i < art.length; i += 4) {
        if (art[i] > 0) {
          hasArtwork = true;
          if (shape && shape[i] === 0) throw new RangeError('The qualified label shape must contain the artwork.');
        }
      }
      check(signal);
      if (performance.now() - lastYield > 8) { await pause(signal); lastYield = performance.now(); }
    }
    return hasArtwork;
  }

  /** Preview only. The caller qualifies the selected offer and its label shape. */
  async function render(sourceCanvas, options) {
    options = options && typeof options === 'object' ? options : {};
    var signal = options.signal;
    check(signal);
    if (!isCanvas(sourceCanvas)) throw new TypeError('The doming source must be an HTMLCanvasElement.');
    var width = sourceCanvas.width, height = sourceCanvas.height, document = sourceCanvas.ownerDocument;
    if (!width || !height || width > LIMITS.maxDimension || height > LIMITS.maxDimension || width * height > LIMITS.maxPixels) {
      throw new RangeError('The doming source exceeds the preview canvas bounds.');
    }
    // Snapshot before yielding: subsequent caller edits cannot alter this job.
    var output = canvas(document, width, height), outputContext = context(output);
    outputContext.drawImage(sourceCanvas, 0, 0);
    if (!eligible(options)) return annotated(output, options);
    var strength = number(options.strength, 0.72, 0, 1), light = lightOptions(options);
    if (!strength || !light.intensity) return annotated(output, options, 'effect-disabled');
    var requestedQuality = options.quality === 'balanced' ? 'medium' : options.quality;
    var quality = Object.prototype.hasOwnProperty.call(LIMITS, requestedQuality) && requestedQuality !== 'maxPixels'
      && requestedQuality !== 'maxDimension' ? requestedQuality : 'medium';
    var scale = Math.min(1, LIMITS[quality] / Math.max(width, height));
    var w = Math.max(1, Math.round(width * scale)), h = Math.max(1, Math.round(height * scale));
    var work = canvas(document, w, h), ctx = context(work);
    ctx.drawImage(output, 0, 0, w, h);
    var artwork = ctx.getImageData(0, 0, w, h).data;
    // Snapshot the shape and primitive options as well as the source.
    var labelShape = options.labelShape && Object.assign({}, options.labelShape);
    var qualified = shapeMask(labelShape, document, w, h, width, height);
    // Every caller-owned value used after yielding has already been copied.
    var previewOptions = { techniqueId: options.techniqueId || options.technique,
      qualified: options.qualified, qualification: options.qualification && Object.assign({}, options.qualification), labelShape: labelShape };
    var shape = qualified && qualified.pixels;
    var supportColor = options.substrateColor || options.supportColor || options.color;
    var contrast = 0.9 + (1 - substrateLuminance(supportColor)) * 0.15;
    var mask = shape || artwork, distance = new Float32Array(w * h);
    var left = w, top = h, right = -1, bottom = -1, hasArtwork = false;
    for (var i = 0; i < distance.length; i++) {
      var alpha = artwork[i * 4 + 3];
      if (alpha > 3) hasArtwork = true;
      // A bad shape must never crop the source or silently become its backing.
      if (shape && alpha > 24 && shape[i * 4 + 3] < 24) {
        throw new RangeError('The qualified label shape must contain the artwork.');
      }
      if (mask[i * 4 + 3] > 3) {
        var px = i % w, py = Math.floor(i / w);
        left = Math.min(left, px); right = Math.max(right, px);
        top = Math.min(top, py); bottom = Math.max(bottom, py);
        distance[i] = Math.min(px + 1, py + 1, w - px, h - py);
      }
    }
    if (qualified) hasArtwork = await validateShape(output, qualified.fullMask, signal);
    else if (!hasArtwork) hasArtwork = await validateShape(output, null, signal);
    if (!hasArtwork) return annotated(output, previewOptions, 'empty-artwork');
    if (right < left) return annotated(output, previewOptions, 'preview-mask-too-small');
    await pause(signal);
    var lastYield = performance.now();
    // Two-pass chamfer field follows holes and disjoint silhouettes. Work is
    // bounded independently of export resolution; the original art stays sharp.
    for (var y = 0; y < h; y++) {
      for (var x = 0; x < w; x++) {
        i = y * w + x;
        if (!distance[i]) continue;
        var d = distance[i];
        if (x) d = Math.min(d, distance[i - 1] + 1);
        if (y) d = Math.min(d, distance[i - w] + 1);
        if (x && y) d = Math.min(d, distance[i - w - 1] + Math.SQRT2);
        if (y && x < w - 1) d = Math.min(d, distance[i - w + 1] + Math.SQRT2);
        distance[i] = d;
      }
      if (y % 16 === 0) {
        check(signal);
        if (performance.now() - lastYield > 8) { await pause(signal); lastYield = performance.now(); }
      }
    }
    for (y = h - 1; y >= 0; y--) {
      for (x = w - 1; x >= 0; x--) {
        i = y * w + x;
        if (!distance[i]) continue;
        d = distance[i];
        if (x < w - 1) d = Math.min(d, distance[i + 1] + 1);
        if (y < h - 1) d = Math.min(d, distance[i + w] + 1);
        if (x < w - 1 && y < h - 1) d = Math.min(d, distance[i + w + 1] + Math.SQRT2);
        if (x && y < h - 1) d = Math.min(d, distance[i + w - 1] + Math.SQRT2);
        distance[i] = d;
      }
      if (y % 16 === 0) {
        check(signal);
        if (performance.now() - lastYield > 8) { await pause(signal); lastYield = performance.now(); }
      }
    }
    var overlay = ctx.createImageData(w, h), pixels = overlay.data;
    var spanX = right - left + 1, spanY = bottom - top + 1;
    var edgeRadius = Math.max(1.2, Math.min(spanX, spanY) * 0.055);
    for (y = top; y <= bottom; y++) {
      for (x = left; x <= right; x++) {
        i = y * w + x; d = distance[i];
        if (!d) continue;
        var dx = ((x < w - 1 ? distance[i + 1] : 0) - (x ? distance[i - 1] : 0)) * 0.5;
        var dy = ((y < h - 1 ? distance[i + w] : 0) - (y ? distance[i - w] : 0)) * 0.5;
        var side = Math.max(-1, Math.min(1, -dx * light.x - dy * light.y));
        var rim = Math.exp(-(d - 0.6) / edgeRadius);
        var u = (x - left + 0.5) / spanX - 0.5, v = (y - top + 0.5) / spanY - 0.5;
        var along = u * light.x + v * light.y, across = -u * light.y + v * light.x;
        var softbox = Math.exp(-Math.pow((along - 0.27) / 0.20, 2) - Math.pow(across / 0.52, 2));
        var shade = (side * 0.13 * rim - 0.025 * rim
          + Math.pow(Math.max(0, side), 4) * 0.16 * rim + softbox * 0.105) * strength * light.intensity * contrast;
        var offset = i * 4, color = shade >= 0 ? 255 : 0;
        pixels[offset] = color; pixels[offset + 1] = color; pixels[offset + 2] = color;
        // Default clipping is performed once, at original resolution, below.
        pixels[offset + 3] = Math.round(Math.min(0.28, Math.abs(shade)) * 255);
      }
      if (y % 16 === 0) {
        check(signal);
        if (performance.now() - lastYield > 8) { await pause(signal); lastYield = performance.now(); }
      }
    }
    ctx.putImageData(overlay, 0, 0);
    // Qualified shapes can show clear resin around an artwork. No opaque base,
    // invented ink, color conversion, file serialization or placement change.
    outputContext.save();
    outputContext.globalCompositeOperation = shape ? 'source-over' : 'source-atop';
    if (qualified && qualified.fullMask) {
      var layer = canvas(document, width, height), layerContext = context(layer);
      layerContext.drawImage(work, 0, 0, width, height);
      layerContext.globalCompositeOperation = 'destination-in';
      layerContext.drawImage(qualified.fullMask, 0, 0);
      outputContext.drawImage(layer, 0, 0);
    } else {
      if (shape) { traceShape(outputContext, labelShape, width, height); outputContext.clip(); }
      outputContext.drawImage(work, 0, 0, width, height);
    }
    outputContext.restore();
    check(signal);
    return annotated(output, previewOptions);
  }

  registry.doming = Object.freeze({ render: render, describe: describe,
    getMetadata: function (output) { return renderMetadata.get(output) || null; }, version: '1.1.0', limits: LIMITS });
}());
